/**
 * Singleton unit toggle. The OPL bulk dataset stores every weight in kg
 * (column suffixes make this explicit — `total_kg`, `bodyweight_kg`, etc.),
 * so all conversion happens at display time. Picking lbs is a session
 * preference that persists to localStorage so a returning lifter who reads
 * in pounds doesn't have to flip it on every visit.
 *
 * Why a singleton ref rather than a provide/inject composable: Chart.js
 * renders inside <canvas>, not Vue's reactivity graph, so we need any
 * helper that touches `.value` to be importable from non-component code
 * (chart-helpers.ts). A module-scoped ref gets that for free, and the
 * Vue components that import it still get full reactivity.
 */

import { ref } from 'vue';
import { track } from '../analytics';

export type Unit = 'kg' | 'lbs';

const STORAGE_KEY = 'pt:unit';
const KG_TO_LBS = 2.20462;

function readStored(): Unit {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'lbs' ? 'lbs' : 'kg';
  } catch {
    // localStorage can throw under privacy mode / disabled cookies. The
    // page must still render — fall back to the kg default silently.
    return 'kg';
  }
}

export const unit = ref<Unit>(readStored());

export function setUnit(next: Unit) {
  if (unit.value === next) return;
  unit.value = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  // Fire an analytics event so we can see in GA how many visitors
  // actually flip to lbs (and from which page they did it).
  track('unit_changed', { unit: next });
}

/**
 * Pure number conversion — kg input → current-unit number. Use this for
 * chart datasets where the result is plotted, not displayed as a string.
 * Returns NaN for null/undefined/non-numeric input so chart.js skips the
 * point rather than rendering it at zero.
 */
export function kgToCur(kg: number | string | null | undefined): number {
  if (kg == null) return NaN;
  const n = Number(kg);
  if (!Number.isFinite(n)) return NaN;
  return unit.value === 'kg' ? n : n * KG_TO_LBS;
}

/**
 * Format a kg value as a display string in the current unit. Returns the
 * passed `fallback` (default '-') for null/undefined/non-numeric input so
 * table cells render a neutral hyphen instead of "NaN lbs".
 */
export function fmtKg(
  kg: number | string | null | undefined,
  opts: { decimals?: number; unitSuffix?: boolean; fallback?: string } = {},
): string {
  const fallback = opts.fallback ?? '-';
  if (kg == null) return fallback;
  const n = Number(kg);
  if (!Number.isFinite(n)) return fallback;
  const value = unit.value === 'kg' ? n : n * KG_TO_LBS;
  // Default 1 decimal — both kg (e.g. 437.5) and lbs (964.5) read cleanly
  // at that precision, and the conversion never introduces meaningful
  // sub-decigram precision anyway.
  const dec = opts.decimals ?? 1;
  const formatted = value.toFixed(dec);
  return opts.unitSuffix === false ? formatted : `${formatted} ${unit.value}`;
}

/** Same as fmtKg but always returns a signed number ("+12.5kg" / "−4.0kg")
 *  — used by delta cells. Sign uses U+2212 (true minus) to match the rest
 *  of the UI's signed-delta rendering. */
export function fmtKgDelta(
  kg: number | string | null | undefined,
  opts: { decimals?: number; unitSuffix?: boolean } = {},
): string {
  if (kg == null) return '-';
  const n = Number(kg);
  if (!Number.isFinite(n)) return '-';
  const value = unit.value === 'kg' ? n : n * KG_TO_LBS;
  const dec = opts.decimals ?? 1;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const formatted = Math.abs(value).toFixed(dec);
  return opts.unitSuffix === false ? `${sign}${formatted}` : `${sign}${formatted} ${unit.value}`;
}

/** Just the unit string for use in chart axis labels: "Total (kg)" / "Total (lbs)". */
export function unitSuffix(): string { return unit.value; }

/**
 * Round-trip kg → display unit and back. Used by `<input v-model>` on
 * weight-class / total filters and the calculators so the user types
 * numbers in their chosen unit while we keep canonical kg in state.
 * kgToDisplay rounds to whole numbers in lbs (never typed at sub-pound
 * precision in practice).
 *
 * displayToKg keeps FULL precision on the way in. Rounding kg here (it
 * previously snapped to 0.1 kg) inflates the value on the way back out:
 * 20 lbs → 9.07 kg rounded to 9.1 kg → displayed back as 20.1 lbs. Full
 * precision round-trips exactly (20 lbs → 9.0718 kg → 20.0 lbs).
 */
export function kgToDisplay(kg: number | undefined): number | undefined {
  if (kg == null) return undefined;
  return unit.value === 'kg' ? kg : Math.round(kg * KG_TO_LBS);
}
export function displayToKg(v: number | undefined): number | undefined {
  if (v == null) return undefined;
  return unit.value === 'kg' ? v : v / KG_TO_LBS;
}
